import React, { useEffect, useRef } from 'react';

interface CassetteLoaderProps {
  size?: number;
  className?: string;
  variant?: 'inline' | 'button' | 'overlay';
  text?: string;
}

function readLoaderColors() {
  const style = getComputedStyle(document.documentElement);
  const pick = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  return {
    body: pick('--loader-body', pick('--color-surface', '#fffef9')),
    ink: pick('--loader-ink', pick('--color-text', '#2c2416')),
    accent: pick('--loader-accent', pick('--brand-from', '#65a30d')),
  };
}

const CassetteLoader: React.FC<CassetteLoaderProps> = ({
  size = 80,
  className = '',
  variant = 'overlay',
  text,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const colorsRef = useRef(readLoaderColors());

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = size;
    canvas.height = size;

    const scale = size / 80;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    const refreshColors = () => {
      colorsRef.current = readLoaderColors();
    };

    refreshColors();
    const observer = new MutationObserver(refreshColors);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    let frame = 0;
    const animate = () => {
      const { body, ink, accent } = colorsRef.current;

      ctx.clearRect(0, 0, 80, 80);

      ctx.fillStyle = body;
      ctx.strokeStyle = ink;
      ctx.lineWidth = 2;
      ctx.fillRect(10, 20, 60, 40);
      ctx.strokeRect(10, 20, 60, 40);

      ctx.fillStyle = ink;
      for (const [x, y] of [[15, 25], [65, 25], [15, 55], [65, 55]] as const) {
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }

      const drawReel = (cx: number) => {
        const angle = (frame * 0.1) % (Math.PI * 2);
        ctx.save();
        ctx.translate(cx, 40);
        ctx.rotate(angle);
        ctx.fillStyle = body;
        ctx.strokeStyle = ink;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(0, 0, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = ink;
        for (let i = 0; i < 6; i += 1) {
          const a = (i * Math.PI) / 3;
          ctx.beginPath();
          ctx.arc(Math.cos(a) * 6, Math.sin(a) * 6, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      };

      drawReel(25);
      drawReel(55);

      ctx.strokeStyle = ink;
      ctx.lineWidth = 1.5;
      ctx.strokeRect(35, 32, 10, 16);

      const tapeVibration = Math.sin(frame * 0.3) * 0.5;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(35, 40 + tapeVibration);
      ctx.lineTo(45, 40 + tapeVibration);
      ctx.stroke();

      ctx.fillStyle = ink;
      for (const x of [37, 40, 43]) {
        ctx.beginPath();
        ctx.arc(x, 40 + tapeVibration, 1, 0, Math.PI * 2);
        ctx.fill();
      }

      frame += 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      observer.disconnect();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [size]);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <canvas ref={canvasRef} className="block" style={{ width: size, height: size }} aria-hidden />
      {text && variant === 'overlay' && (
        <div className="text-sm font-medium" style={{ color: 'var(--color-muted)' }}>{text}</div>
      )}
    </div>
  );
};

export default CassetteLoader;
